import { useEffect, useRef } from 'react';

/**
 * Escape closes the modal on top — and only the one on top.
 *
 * A listener per modal is not enough on its own: the modes panel opens over
 * settings, and both listeners hear the same Escape, so a naive version
 * collapses the whole stack and drops you back on the board. Every open modal
 * pushes onto this shared stack and answers only while it is last, so one
 * Escape peels off one layer.
 *
 * `isAnyModalOpen()` is the other half. Game.js binds Escape to deselecting a
 * robot, and that must not fire underneath an open modal — asking the stack
 * costs it one check that never needs revisiting, where a ref per modal has
 * to be remembered every time somebody adds one.
 */
const stack = [];

export function isAnyModalOpen() {
  return stack.length > 0;
}

export default function useModalEscape(open, onClose) {
  // Call sites write `onClose={() => setOpen(false)}`, a new function every
  // render. Depending on it would push and pop the stack on each one and lose
  // the ordering the stack exists for, so the effect reads it through a ref.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return undefined;

    const token = {};
    stack.push(token);

    const handler = (e) => {
      if (e.key !== 'Escape') return;
      if (stack[stack.length - 1] !== token) return;
      onCloseRef.current?.();
    };
    window.addEventListener('keydown', handler);

    return () => {
      window.removeEventListener('keydown', handler);
      const at = stack.indexOf(token);
      if (at !== -1) stack.splice(at, 1);
    };
  }, [open]);
}
